import style from './usos-dialog.scss';

import { html, LitElement } from "lit";
import { customElement, state, property, query, queryAssignedNodes } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { localized, msg, str } from "@lit/localize";

import "@a11y/focus-trap/focus-trap";

import "../icon/usos-icon";
import { DynamicArea } from "../dynamic/dynamic-area";

/**
 * Accessible dialog with an option to load content dynamically by the HTTP GET request.
 *
 * @slot title - dialog title
 * @slot title-trailing-content - extra title content to be displayed next to the close button
 * @slot - dialog contents
 * @fires {Event} dialog:open - when dialog is opened
 * @fires {Event} dialog:cancel - when dialog is about to be closed, cancellable
 * @fires {Event} dialog:close - when dialog is closed
 * @fires {Event} dialog:urlLoaded - when dialog loaded its dynamic contents
 */
@customElement('usos-dialog')
@localized()
export class UsosDialog extends LitElement {
    static override styles = style;


    /**
     * Whether the dialog content should be loaded dynamically.
     */
    @property({type: Boolean}) dynamic = false;

    /**
     * Dynamic contents' URL. Required if `dynamic` is true.
     */
    @property() url = '';

    /**
     * Name of icon from Material Icons displayed in front of dialog's title.
     * For possible name option {@see UsosIcon.iconName}.
     */
    @property({attribute: 'title-icon-name'}) titleIconName = '';

    /**
     * Whether the dialog should create button that opens it.
     */
    @property({attribute: 'with-button', type: Boolean}) withButton = false;

    /**
     * If with-button then this attribute contains button text content.
     */
    @property({attribute: 'button-label'}) buttonLabel = '';

    /**
     * If with-button then this attribute contains button class attribute.
     * Possible classes are those defined by USOS-UI: positive | neutral | negative and outlined.
     */
    @property({attribute: 'button-class'}) buttonClass = '';

    /**
     * If with-button then whether button should be disabled.
     * Works only with <button> mode, not with <usos-icon is-button>.
     */
    @property({attribute: 'button-disabled', type: Boolean}) buttonDisabled = false;

    /**
     * If with-button then icon-button replaces button with <usos-icon is-button>.
     */
    @property({attribute: 'icon-button', type:Boolean}) iconButton = false;

    /**
     * Icon name used for button or usos-icon that opens dialog.
     * For possible name option {@see UsosIcon.iconName}.
     */
    @property({attribute: 'button-icon-name'}) buttonIconName = '';


    @state() private opened = false;

    private static openDialogs: UsosDialog[] = [];

    private _dynamicArea: Optional<DynamicArea> = null;

    @query('dialog') private _dialog!: HTMLDialogElement;
    @query('#close') private _closeButton!: HTMLButtonElement;
    @query('#content') private _contentContainer!: HTMLDivElement;
    @queryAssignedNodes() private _contentSlotAssignedList!: Node[];
    @queryAssignedNodes({slot: 'title'}) private _titleSlotAssignedList!: Node[];


    constructor() {
        super();
        this.addEventListener('usos-ui:lockScroll', this._onLockScroll);
        this.addEventListener('usos-ui:unlockScroll', this._onUnlockScroll);
    }


    override render() {
        return html`
            ${when(this.withButton, () => this._renderButton())}
            <dialog aria-labelledby='title' aria-describedby='content'>
                <focus-trap>
                    <div id="titlebar">
                        ${when(this.titleIconName, () => html`<usos-icon icon-name=${this.titleIconName}></usos-icon>`)}
                        <span id="title"><slot name="title"></slot></span>
                        <slot name="title-trailing-content"></slot>
                        <button id="close" @click=${this.close} aria-label=${msg(str`Zamknij`)}>
                        </button>
                    </div>
                    <div id="content">
                        <slot></slot>
                    </div>
                </focus-trap>
            </dialog>
        `;
    }

    private _renderButton(){
        const desc = html`
            <span class="sr-only" id="dialog_desc">${msg(str`Otwiera okno dialogowe.`)}</span>
        `;

        if (this.iconButton) {
            return html`
                ${desc}
                <usos-icon is-button icon-name=${this.buttonIconName}
                    @click=${this.open} aria-describedby="dialog_desc"
                    label=${this.buttonLabel}></usos-icon>
            `;
        } else {
            return html`
                ${desc}
                <span class="usos-ui">
                    <button type="button" class=${this.buttonClass} @click=${this.open} aria-describedby="dialog_desc"
                        .disabled=${this.buttonDisabled}>
                        ${when(this.buttonIconName, () => html`
                            <usos-icon icon-name=${this.buttonIconName}></usos-icon>
                        `)}
                        ${this.buttonLabel}
                    </button>
                </span>
            `;
        }
    }

    override firstUpdated() {
        this._dialog.addEventListener('cancel', evt => {
            if (this.cancel() === false) {
                evt.preventDefault();
            }
        })
    }


    /**
     * Open this dialog.
     */
    open() {
        if (this.opened) throw new Error('Cannot open already opened UsosDialog');

        if (this.dynamic) {
            this._dynamicArea = new DynamicArea();
            this._dynamicArea.url = this.url;
            this._dynamicArea.autoload = true;
            this._dynamicArea.addEventListener('dynamic-area:urlLoaded', evt => {
                this._onUrlLoaded(evt);
            });
            this.appendChild(this._dynamicArea);
        }
        this._dialog.showModal();
        this.opened = true;
        UsosDialog.openDialogs.push(this);
        this._lockScroll();
        this._closeButton.focus();
        this._dialog.addEventListener('close', this._onClose.bind(this), { once: true });
        this.dispatchEvent(new Event('dialog:open', { composed: true, bubbles: true }));
    }

    openWithContents(content: string) {
        const blob = new Blob([content]);
        const url = URL.createObjectURL(blob);
        this.url = url;
        this.dynamic = true;
        this.open();
        this.addEventListener('dialog:urlLoaded', () => {
            URL.revokeObjectURL(url);
        }, { once: true });
    }

    isOpened() {
        return this.opened;
    }

    /**
     * Close this dialog.
     * @fires cancel on HTMLDialogElement (and dialog:cancel on this) to enable preventing
     */
    close() {
        if (!this.opened) throw new Error('Cannot close already closed UsosDialog');

        const evt = new Event('cancel', { composed: true, bubbles: true, cancelable: true});
        this._dialog.dispatchEvent(evt);
        if (evt.defaultPrevented) return false;

        this._dialog.close();
    }


    private cancel() {
        const evt = new Event('dialog:cancel', { composed: true, bubbles: true, cancelable: true});
        this.dispatchEvent(evt);

        if (evt.defaultPrevented) return false;
    }

    private _onClose() {
        this.opened = false;
        const idx = UsosDialog.openDialogs.indexOf(this);
        if (idx != -1) {
            this._unlockScroll();
            UsosDialog.openDialogs.splice(idx, 1);
        }
        if (this.dynamic) {
            this._contentSlotAssignedList.forEach(node => this.removeChild(node));
            this._dynamicArea = null;
        }
        this.dispatchEvent(new Event('dialog:close', { composed: true, bubbles: true }));
    }

    /**
     * Locks scroll on self in response to usos-ui:lockScroll event.
     */
    private _onLockScroll(evt: HTMLElementEventMap['usos-ui:lockScroll']) {
        this._contentContainer.style.overflow = 'hidden';
        if (evt.detail?.single) evt.stopImmediatePropagation();
    }

    /**
     * Unlocks scroll on self in response to usos-ui:unlockScroll event.
     */
    private _onUnlockScroll(evt: HTMLElementEventMap['usos-ui:unlockScroll']) {
        this._contentContainer.style.overflow = '';
        if (evt.detail?.single) evt.stopImmediatePropagation();
    }

    /**
     * Changes dynamic-area:urlLoaded event into dialog:urlLoaded event
     * and looks for dynamic title element in loaded content.
     */
    private _onUrlLoaded(evt: HTMLElementEventMap['dynamic-area:urlLoaded']) {
        evt.stopImmediatePropagation();

        const title = this._searchForTitle();
        if (title) {
            this._titleSlotAssignedList.forEach(node => this.removeChild(node));
            this.append(title);
        }

        this.dispatchEvent(
            new Event('dialog:urlLoaded', { composed: true, bubbles: true })
        );
    }


    private _searchForTitle() : Optional<Element>{
        const title = this._dynamicArea?.querySelector('[data-slot=title]');
        if (title){
            title.parentNode!.removeChild(title);
            title.slot = 'title';
            return title;
        }
        return null;
    }

    private _lockTarget() {
        /*
         * There are two possible situations:
         * 1. I was opened while another UsosDialog was active so I need to lock scroll in the most recently opened
         *    UsosDialog because there was no one to do that before.
         * 2. I am the only opened UsosDialog so I should lock whoever opened me.
         * The latter is easy and I could even dispatch event on myself.
         * The former is more tricky as I do not know anything about that another UsosDialog - it might be my ancestor
         * or not - eg. it might be opened from DialogToolbox. It means that in this case dispatching event on myself
         * is not an option.
         * getRootNode() is fallback if I am immediate child within some shadowRoot that I cannot leave
         */
        if (UsosDialog.openDialogs.length > 1) return UsosDialog.openDialogs.slice(-2, -1)[0];
        else return this.parentElement || this.getRootNode();
    }

    /**
     * Locks scroll on document.
     */
    private _lockScroll() {
        const lockTarget = this._lockTarget();
        lockTarget.dispatchEvent(new CustomEvent('usos-ui:lockScroll', {
            bubbles: true,
            composed: true,
            detail: { single: true }
        }));
    }

    /**
     * Unlocks scroll on document.
     */
    private _unlockScroll() {
        const lockTarget = this._lockTarget();
        lockTarget.dispatchEvent(new CustomEvent('usos-ui:unlockScroll', {
            bubbles: true,
            composed: true,
            detail: { single: true }
        }));
    }

 }

declare global {
    interface HTMLElementTagNameMap {
      "usos-dialog": UsosDialog,
    }
    interface HTMLElementEventMap {
        'dialog:open': Event,
        'dialog:cancel': Event,
        'dialog:close': Event,
        'dialog:urlLoaded': Event,
    }
}
